import { makeAutoObservable, makeObservable, observable } from 'mobx'

import { EMPTY_NUMBER_ID, EMPTY_STRING_ID } from '@/constant/common.ts'
import type { Student } from '@/dto'
import { DTO } from '@/dto'
import type CheckableMap from '@/models/CheckableMap.ts'
import dateUtils from '@/utils/DateUtils.ts'

export type AchievementReportData = Omit<res.AchievementReport, 'achievementConceptChips'> & {
  studentName: Student['name']
}

export class AchievementReport extends DTO implements AchievementReportData {
  id = EMPTY_NUMBER_ID
  title = ''
  studentName = ''
  comment = ''
  createDatetime = EMPTY_STRING_ID
  pdfUrl = ''
  status: 'FAIL' | 'SUCCESS' | 'GENERATING' = 'GENERATING'

  constructor(data: Partial<AchievementReportData>) {
    super()
    makeObservable(this, {
      id: observable,
      title: observable,
      studentName: observable,
      comment: observable,
      createDatetime: observable,
      pdfUrl: observable,
      status: observable,
    })
    this.update(data)
  }

  get formattedCreateDatetime() {
    return dateUtils.toSimpleDateString(new Date(this.createDatetime), '.')
  }
}

export class AchievementReportColl {
  constructor(private reportMap: CheckableMap<AchievementReport['id'], AchievementReport>) {
    makeAutoObservable(this)
  }

  get toArr() {
    return this.reportMap.toArr
  }

  get toCheckedKeys() {
    return this.reportMap.toCheckedKeys
  }

  get toCheckedValues() {
    return this.reportMap.toCheckedValues
  }

  get isAllChecked() {
    return this.reportMap.isFullyChecked
  }

  get isPartChecked() {
    return this.reportMap.isPartChecked
  }

  get checkSize() {
    return this.reportMap.checkSize
  }

  get isEmpty() {
    return this.reportMap.isEmpty
  }

  get(key: AchievementReport['id']) {
    return this.reportMap.get(key)
  }

  toggleCheck(key: number) {
    this.reportMap.toggleCheck(key)
  }

  isChecked(key: number) {
    return this.reportMap.isChecked(key)
  }

  clearAllCheck() {
    this.reportMap.clearAllCheck()
  }

  allCheck() {
    this.reportMap.allCheck()
  }

  toggleCheckAll() {
    this.isAllChecked ? this.reportMap.clearAllCheck() : this.reportMap.allCheck()
  }

  setCheck(id: AchievementReport['id']) {
    this.reportMap.setCheck(id)
  }

  set(k, v) {
    this.reportMap.set(k, v)
  }
}
